<template>
  <teleport to="#end-of-body">
    <div
      :class="[
        transitionFromBottom ? 'drawer-bottom' : 'drawer-right',
        containerClass
      ]">
      <fade-transition>
        <div
          v-if="isOpen && !userControlledHeight"
          :class="backdropClasses"
          class="fixed pin drawer-backdrop"
          @click="closeDrawerFromBackground"></div>
      </fade-transition>
      <drawer-transition :position="transitionDirection">
        <div
          v-if="isOpen"
          ref="drawerEl"
          :style="userControlledHeight ? { height: drawerHeight } : {}"
          class="border br-t-2 md:br-0 border-transparent fixed bottom-0 right-0 drawer flex flex-col inner-drawer height-transition drawer-container"
          :class="[topAlignment, backgroundColourClass, collapseClass, size]">
          <div
            v-if="transitionFromBottom && userControlledHeight"
            ref="dragHandle"
            class="drawer-drag-handle"
            @mousedown="startDragging"
            @touchstart.passive="startDragging">
            <div class="drawer-handle mx-auto absolute w-2/3"></div>
          </div>
          <div
            ref="drawerContent"
            :class="contentClasses">
            <div
              v-if="$slots.header"
              class="sticky z-6 px-4 md:px-6 pt-4 md:pt-6 pb-3 bg-paper-1"
              :class="headerClasses"
              style="top: -1px">
              <div class="md:p-3 flex flex-col">
                <div
                  v-if="!requiresInteraction && !hideDefaultCloseIcon"
                  class="self-end"
                  @click="manuallyCloseDrawer">
                  <svg-icon
                    class="cursor-pointer"
                    height="24px"
                    width="24px"
                    name="close-display" />
                </div>
                <slot name="header"></slot>
              </div>
            </div>
            <div
              v-if="$slots.content"
              class="px-4 md:px-6 pb-4 md:pb-6"
              :class="contentFillClasses">
              <div
                class="md:px-3"
                :class="contentFillClasses">
                <slot name="content"></slot>
              </div>
            </div>
          </div>
          <div v-if="$slots.controls">
            <slot name="controls"></slot>
          </div>
        </div>
      </drawer-transition>
    </div>
  </teleport>
</template>

<script>
import SvgIcon from "@/app/javascript/components/shared/SvgIcon.vue";
import DrawerTransition from "@/app/javascript/components/transitions/DrawerTransition.vue";
import FadeTransition from "@/app/javascript/components/transitions/FadeTransition.vue";

export default {
  name: "Drawer",
  components: {
    SvgIcon,
    DrawerTransition,
    FadeTransition
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    transitionFromBottom: {
      type: Boolean,
      default: false
    },
    requiresInteraction: {
      type: Boolean,
      default: false
    },
    showBackdrop: {
      type: Boolean,
      default: true
    },
    preventBackgroundClose: {
      type: Boolean,
      default: false
    },
    fullHeight: {
      type: Boolean,
      default: false
    },
    backgroundColour: {
      type: String,
      default: "paper-1"
    },
    quickCollapse: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: "normal"
    },
    userControlledHeight: {
      type: Boolean,
      default: false
    },
    drawerHeight: {
      type: String,
      default: "380px"
    },
    containerClass: {
      type: String,
      default: ""
    },
    hideDefaultCloseIcon: {
      type: Boolean,
      default: false
    },
    headerClasses: {
      type: String,
      default: ""
    },
    // Stops the content scrolling and lets slot content flex to fit instead.
    fitContent: {
      type: Boolean,
      default: false
    }
  },
  emits: ["closeDrawer", "update:drawerHeight"],
  data() {
    return {
      collapsed: false,
      isDragging: false,
      startY: 0,
      eventListenersAdded: false
    };
  },
  computed: {
    backdropClasses() {
      return {
        "bg-ink": this.showBackdrop,
        "opacity-backdrop": this.showBackdrop && !this.collapsed,
        "opacity-25": this.collapsed
      };
    },
    collapseClass() {
      return this.collapsed ? "collapsed" : "";
    },
    contentClasses() {
      if (this.fitContent) {
        return "flex-1 basis-auto min-h-0 overflow-y-auto flex flex-col";
      }
      return "flex-1 overflow-auto";
    },
    contentFillClasses() {
      return this.fitContent ? "flex-1 basis-auto min-h-0 flex flex-col" : "";
    },
    transitionDirection() {
      if (this.transitionFromBottom) return "bottom";
      return "right";
    },
    topAlignment() {
      return this.transitionFromBottom ? this.bottomDrawerHeight : "top-0";
    },
    backgroundColourClass() {
      return `bg-${this.backgroundColour}`;
    },
    bottomDrawerHeight() {
      if (this.fullHeight) {
        return "h-full";
      }

      if (this.userControlledHeight) {
        return "user-controlled-height";
      }

      return "max-height";
    }
  },
  watch: {
    isOpen(newVal) {
      const mmCornerPeel = document.querySelector(".mm-corner-peel");
      const emailPopUp = document.querySelector(".marketing-pop-up-container");

      this.toggleHiddenClass(mmCornerPeel, newVal);
      this.toggleHiddenClass(emailPopUp, newVal);
      this.toggleChatVisibility(newVal);

      document.documentElement.classList.toggle("overflow-y-hidden", newVal);
      document.body.classList.toggle("overflow-y-hidden", newVal);
      document.documentElement.classList.toggle("md:overflow-y-unset", newVal);
      document.body.classList.toggle("md:overflow-y-unset", newVal);
    }
  },
  created() {
    const onKeydown = e => {
      if (this.isOpen && e.keyCode === 27) {
        this.$emit("closeDrawer");
      } else if (e.key === "Alt" && this.quickCollapse) {
        this.collapsed = true;
      }
    };

    const onKeyUp = () => {
      this.collapsed = false;
    };
    document.addEventListener("keydown", onKeydown);
    document.addEventListener("keyup", onKeyUp);
  },
  mounted() {
    document.addEventListener("mousemove", this.handleDragging);
    document.addEventListener("mouseup", this.stopDragging);
    document.addEventListener("touchmove", this.handleDragging, {
      passive: false
    });
    document.addEventListener("touchend", this.stopDragging);
    window.addEventListener(
      "changeDrawerContentScroll",
      this.changeDrawerContentScroll
    );
  },
  unmounted() {},
  methods: {
    changeDrawerContentScroll({ detail }) {
      if (!detail.yAxisDifference) return;
      if (!this.$refs.drawerContent) return;
      let newScrollTop =
        this.$refs.drawerContent.scrollTop - detail.yAxisDifference;
      newScrollTop = Math.max(0, newScrollTop);
      this.$refs.drawerContent.scrollTop = newScrollTop;
    },
    toggleHiddenClass(element, isHidden) {
      if (element) {
        if (isHidden) {
          element.classList.add("hidden");
        } else {
          element.classList.remove("hidden");
        }
      }
    },
    toggleChatVisibility(isHidden) {
      if (isHidden) {
        document.body.classList.add("hide-chat");
      } else {
        document.body.classList.remove("hide-chat");
      }
    },
    manuallyCloseDrawer() {
      if (!this.requiresInteraction) {
        this.$emit("closeDrawer");
      }
    },
    closeDrawerFromBackground() {
      if (!this.preventBackgroundClose) {
        this.manuallyCloseDrawer();
      }
    },
    startDragging(event) {
      this.$refs.drawerEl.classList.remove("height-transition");
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;
      this.startY = clientY;
      this.startHeight = this.$refs.drawerEl.getBoundingClientRect().height;

      this.isDragging = true;
    },
    handleDragging(event) {
      if (!this.isDragging) return;
      event.preventDefault();
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;
      const delta = this.startY - clientY;
      const newHeight = Math.min(
        window.innerHeight,
        Math.max(32, this.startHeight + delta)
      );
      this.$emit("update:drawerHeight", `${newHeight}px`);

      if (newHeight <= 32) {
        this.$emit("closeDrawer");
      }
    },
    stopDragging() {
      this.isDragging = false;
      if (this.$refs.drawerEl) {
        this.$refs.drawerEl.classList.add("height-transition");
      }
    }
  }
};
</script>

<style lang="scss">
.drawer {
  width: 100%;
  z-index: 1000001;

  @media (min-width: $screen-md-min) {
    z-index: 1000001;
    overflow-y: scroll;
    overflow-x: hidden;

    &.normal {
      width: 400px;
    }

    &.extra-large {
      width: 900px;
    }
  }
}

.drawer-backdrop {
  z-index: 1000000;
  transition: opacity 0.1s;
}

// Drawers all share one z-index, so a drawer opened from inside another drawer
// (eg. a confirmation) can't dim the one underneath. This lifts the whole group
// — drawer and backdrop — into a stacking context above the other drawers.
.drawer-above-drawers {
  position: relative;
  z-index: 1000002;
}

.drawer-bottom {
  .max-height {
    max-height: calc(100svh - 80px);
  }

  .user-controlled-height {
    max-height: calc(100svh - 30%);
    box-shadow: 0 4px 16px 0 rgba(0, 12, 27, 0.08);
    border-top-right-radius: 12px;
    border-top-left-radius: 12px;
  }
}

.drawer-drag-handle {
  width: 100%;
  min-height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  touch-action: none;

  .drawer-drag-bar {
    width: 40px;
    height: 4px;
    background-color: #ccc;
    border-radius: 2px;
  }
}

.inner-drawer {
  transition: transform 0.3s ease-in-out;
}

.collapsed {
  transform: translate(100%, 0);
}

.drawer-handle {
  height: 4px;
  width: 32px;
  border-radius: 3px 3px 3px 3px;
  background-color: #c7c9c9;
  right: calc(50% - 15px);
}

.height-transition {
  transition: height 0.3s ease;
}
</style>
